import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import Icon from '../components/Icon';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const AuctionDetails = () => {
  const { auctionId } = useParams();
  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  const fetchAuctionAndBids = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch auction details
      const auctionData = await apiService.getAuction(auctionId);
      setAuction(auctionData);

      // Fetch auction bids
      const bidsData = await apiService.getAuctionBids(auctionId);
      setBids(bidsData.bids || bidsData.results || bidsData || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching auction data:', err);
      setError('Failed to load auction data');
    } finally {
      setLoading(false);
    }
  }, [auctionId]);

  useEffect(() => {
    fetchAuctionAndBids();
  }, [fetchAuctionAndBids]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Check if auction time has passed
  const isAuctionTimeOver = useCallback(() => {
    if (!auction?.end_date) return false;
    const now = new Date();
    const endDate = new Date(auction.end_date);
    return now > endDate;
  }, [auction?.end_date]);

  const sortedBids = React.useMemo(() => {
    let sortableBids = [...(Array.isArray(bids) ? bids : [])];
    const timeOver = isAuctionTimeOver();

    // If auction is completed, sort winner to top first
    if ((auction?.status === 'completed' || timeOver) && auction?.final_winner) {
      const winnerId = auction.final_winner.id;
      const winnerBid = sortableBids.find(bid => bid.user?.id === winnerId);
      const otherBids = sortableBids.filter(bid => bid.user?.id !== winnerId);

      if (winnerBid) {
        sortableBids = [winnerBid, ...otherBids];
      }
    }

    // Then apply regular sorting if specified
    if (sortConfig.key !== null) {
      // If winner is at top, sort only the non-winner bids
      const hasWinnerAtTop = (auction?.status === 'completed' || timeOver) && auction?.final_winner;
      const startIndex = hasWinnerAtTop ? 1 : 0;
      const bidsToSort = sortableBids.slice(startIndex);

      bidsToSort.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'firstName':
            aValue = a.user?.first_name || '';
            bValue = b.user?.first_name || '';
            break;
          case 'lastName':
            aValue = a.user?.last_name || '';
            bValue = b.user?.last_name || '';
            break;
          case 'email':
            aValue = a.user?.email || '';
            bValue = b.user?.email || '';
            break;
          case 'bidAmount':
            aValue = a.bid_amount || 0;
            bValue = b.bid_amount || 0;
            break;
          case 'bidTime':
            aValue = a.created_at || '';
            bValue = b.created_at || '';
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });

      if (hasWinnerAtTop) {
        sortableBids = [sortableBids[0], ...bidsToSort];
      } else {
        sortableBids = bidsToSort;
      }
    }

    return sortableBids;
  }, [bids, sortConfig, auction, isAuctionTimeOver]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };


  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="auctions" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div>Loading auction data...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="auctions" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
              <div>{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="auctions" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to Auctions" to="/staff/auctions" />
          <h1 className="staff-page-header">Auction Details: {auction?.reward?.title}</h1>
          <p className="staff-page-subheader">View all bids for this auction in chronological order.</p>

          {/* Auction Status Banner */}
          {auction && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              margin: '24px 0',
              padding: '16px 24px',
              backgroundColor: '#F8F9FA',
              borderRadius: '12px',
              border: '1px solid #E9ECEF'
            }}>
              <div style={{ display: 'flex', gap: '32px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Starting Bid</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{(auction.starting_bid || 0).toLocaleString()} pts</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    {isAuctionTimeOver() ? 'Final Bid' : 'Current High Bid'}
                  </div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: isAuctionTimeOver() ? '#4CAF50' : '#2196F3'
                  }}>
                    {(auction.current_highest_bid || auction.starting_bid || 0).toLocaleString()} pts
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Total Bids</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{auction.total_bids || 0}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>End Time</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formatDate(auction.end_date)}</div>
                </div>
              </div>

              {(auction.status === 'completed' || isAuctionTimeOver()) && auction.final_winner && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 24px',
                  backgroundColor: '#E8F5E8',
                  border: '2px solid #4CAF50',
                  borderRadius: '100px',
                  color: '#2E7D32',
                  fontSize: '14px',
                  fontWeight: '700'
                }}>
                  <div style={{
                    filter: 'brightness(0) saturate(100%) invert(32%) sepia(85%) saturate(449%) hue-rotate(82deg) brightness(94%) contrast(95%)',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Icon name="checkmark" size={16} />
                  </div>
                  Winner: {auction.final_winner.first_name} {auction.final_winner.last_name}
                </div>
              )}
            </div>
          )}

          <h3 style={{
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: 'normal',
            marginTop: '32px',
            marginBottom: '4px',
          }}>
            All Bids ({sortedBids.length})
          </h3>
          <p style={{
            color: 'var(--Black, #000)',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: '340',
            lineHeight: 'normal',
            opacity: '0.5',
            marginTop: '0',
            marginBottom: '16px'
          }}>
            Complete bid history for this auction, sorted by bid time
          </p>

          <div style={{
            display: 'flex',
            padding: '18px',
            alignItems: 'center',
            alignSelf: 'stretch',
            borderRadius: '12px 12px 0 0',
            background: '#F8F8F8',
            backdropFilter: 'blur(22.5px)'
          }}>
            <div
              onClick={() => handleSort('firstName')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '20%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              First Name
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('lastName')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '20%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Last Name
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('email')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '30%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Email
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('bidAmount')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '15%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Bid Amount
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('bidTime')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '15%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Bid Time
              <Icon name="up-down" size={12} />
            </div>
          </div>

          <div style={{ marginBottom: '8px' }}></div>

          {sortedBids.length === 0 ? (
            <div style={{
              display: 'flex',
              width: '100%',
              maxWidth: '1270px',
              height: '59px',
              padding: '18px',
              alignItems: 'center',
              borderRadius: '12px',
              background: '#F8F8F8',
              marginBottom: '8px',
              justifyContent: 'center',
              fontFamily: '"Baikal Book"',
              fontSize: '14px',
              color: '#6b7280'
            }}>
              No bids found for this auction.
            </div>
          ) : (
            sortedBids.map((bid, index) => {
              const timeOver = isAuctionTimeOver();
              const isWinner = (auction?.status === 'completed' || timeOver) && auction?.final_winner && bid.user?.id === auction.final_winner.id;
              const isHighestBid = bid.is_winning;

              return (
                <div key={bid.id || index} style={{
                  display: 'flex',
                  width: '100%',
                  maxWidth: '1270px',
                  height: '59px',
                  padding: '18px',
                  alignItems: 'center',
                  borderRadius: '12px',
                  background: isWinner ? '#E8F5E8' : (isHighestBid && timeOver ? '#E8F5E8' : (isHighestBid ? '#E3F2FD' : '#F8F8F8')),
                  border: isWinner ? '2px solid #4CAF50' : (isHighestBid && timeOver ? '2px solid #4CAF50' : (isHighestBid ? '2px solid #2196F3' : 'none')),
                  marginBottom: '8px',
                  boxShadow: isWinner ? '0 2px 8px rgba(76, 175, 80, 0.2)' : (isHighestBid && timeOver ? '0 2px 8px rgba(76, 175, 80, 0.2)' : (isHighestBid ? '0 2px 8px rgba(33, 150, 243, 0.2)' : 'none'))
                }}>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    width: '20%',
                    minWidth: '0',
                    flexShrink: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '59px',
                    paddingRight: '12px'
                  }}>
                    {bid.user?.first_name || 'N/A'}
                  </div>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    width: '20%',
                    minWidth: '0',
                    flexShrink: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '59px',
                    paddingRight: '12px'
                  }}>
                    {bid.user?.last_name || 'N/A'}
                  </div>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    width: '30%',
                    minWidth: '0',
                    flexShrink: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '59px',
                    paddingRight: '12px'
                  }}>
                    {bid.user?.email || 'N/A'}
                  </div>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    width: '15%',
                    minWidth: '0',
                    flexShrink: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '59px',
                    paddingRight: '12px',
                    fontWeight: isHighestBid ? 'bold' : 'normal',
                    color: isHighestBid && timeOver ? '#4CAF50' : (isHighestBid ? '#2196F3' : 'inherit')
                  }}>
                    {(bid.bid_amount || 0).toLocaleString()} pts
                  </div>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    width: '15%',
                    minWidth: '0',
                    flexShrink: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '59px',
                    paddingRight: '12px'
                  }}>
                    {formatDate(bid.created_at)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AuctionDetails;